import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  TextField,
  InputLabel,
  Select,
  Button,
  FormHelperText,
  Grid,
  CircularProgress,
  Paper,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Modal,
  Fade,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Header from "../../../components/Header";
import Menu from "../../../components/Menu";
import Footer from "../../../components/Footer";
import ContentContainer from "../../../components/ContentContainer";
import { generatePdfBase64 } from "./pdfGenerator";
import { UserContext } from "../../../contexts/UserContext";
import "./ContratoDigitalPage.css";

const ModificarContratoDigitalPage = () => {
  const { token, roleId } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const pdfViewerRef = useRef(null);

  // Componente de input memoizado

  // ESTADOS PARA LA BÚSQUEDA Y SELECCIÓN DEL CONTRATO
  const [searchRut, setSearchRut] = useState("");
  const [contracts, setContracts] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [planVelocidadExtraido, setPlanVelocidadExtraido] = useState(null);

  // ESTADO PARA LA VISTA PREVIA DEL PDF
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // ESTADO DEL FORMULARIO
  const [formData, setFormData] = useState({
    // DATOS PERSONALES – FACTURACIÓN
    nombre_cliente: "",
    calle_facturacion: "",
    cond_edif_facturacion: "",
    comuna_facturacion: "",
    email: "",
    numero: "",
    tel_trabajo: "",
    // DATOS PERSONALES – CONEXIÓN
    rut_cliente: "",
    rur_representante_legal: "",
    folio: "",
    folio_nuevo: "", // Campo para el nuevo folio en cambio de plan
    nro_cliente: "",
    fecha_contrato: null,
    fecha_nacimiento: null,
    depto: "",
    sector: "",
    nodo: "",
    tel_movil: "",
    tel_movil2: "",
    razon_social: "",
    calle_numero: "",
    region_conexion: "",
    comuna_conexion: "",
    tipo_documento: "Boleta",
    envio_documento: "Correo Electrónico",
    autorizacion_envio: false,
    tel_fijo: "",
    // SERVICIOS – PLAN COMERCIAL
    servicio: "Internet",
    plan_servicio: "",
    promocion: "",
    observaciones_plan: "",
    promocion_lista: "",
    velocidad_promocion: "",
    dia_pago: "Día 10 de cada mes",
    primer_pago: null,
    // RENTA MENSUAL
    renta_mensual_plan_comercial: "",
    renta_mensual_servicios_adicionales: "0",
    total_renta_mensual_plan_contratado: "",
    costo_instalacion_servicio_principal: "0",
    costo_instalacion_otros: "0",
    total_costos_instalacion: "",
    total_a_pagar: "",
    plan_seleccionado: "",
    // DOCUMENTOS ACREDITADOS
    doc_fotocopia_constitucion: false,
    doc_fotocopia_rut_representante: false,
    doc_fotocopia_cedula: false,
    doc_comprobante_domicilio: false,
    doc_otro: "",
    doc_condiciones_generales: false,
    doc_contrato_servicios: false,
    doc_contrato_comodato: false,
    doc_informacion_facturacion: false,
  });

  // const [localInputs, setLocalInputs] = useState({});

  // ESTADOS PARA CATÁLOGOS
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [firmantes, setFirmantes] = useState([]);
  const [selectedSignerId, setSelectedSignerId] = useState(null);
  const [commercialPlans, setCommercialPlans] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [nextFolio, setNextFolio] = useState(null);

  // Función para obtener el próximo folio disponible
  async function fetchNextFolio() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/digital-contracts/next-folio`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setNextFolio(data.nextFolio);
      }
    } catch (error) {
      console.error("Error al obtener próximo folio:", error);
      enqueueSnackbar("Error al obtener próximo folio", { variant: "error" });
    }
  }

  // EXPANDIR MENÚ AL MONTARSE
  useEffect(() => {
    sessionStorage.setItem("menuAdminExpanded", "true");
    fetchNextFolio();
  }, [token, enqueueSnackbar]);

  // FETCH DE REGIONES
  const fetchRegions = useCallback(async () => {
    setLoadingRegions(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/regions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        enqueueSnackbar("Error al cargar las regiones", { variant: "error" });
        return;
      }
      const data = await response.json();
      setRegions(data);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error de conexión al cargar regiones", {
        variant: "error",
      });
    } finally {
      setLoadingRegions(false);
    }
  }, [token, enqueueSnackbar]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  // FETCH DE COMUNAS
  const loadCommunes = useCallback(
    async (region) => {
      setLoadingCommunes(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/communes/communes/${region}`,
          { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" }
        );
        if (!response.ok) {
          enqueueSnackbar("Error al cargar las comunas", { variant: "error" });
          setCommunes([]);
          return [];
        }
        const data = await response.json();
        setCommunes(data);
        return data;
      } catch (error) {
        console.error("Error cargando comunas:", error);
        enqueueSnackbar("Error de conexión al cargar comunas", {
          variant: "error",
        });
        setCommunes([]);
        return [];
      } finally {
        setLoadingCommunes(false);
      }
    },
    [token, enqueueSnackbar]
  );

  useEffect(() => {
    if (formData.region_conexion) {
      loadCommunes(formData.region_conexion);
    }
  }, [formData.region_conexion, loadCommunes]);

  // FETCH DE PLANES COMERCIALES (PROMOCIONES)
  const fetchPromotions = useCallback(async () => {
    if (!formData.comuna_conexion) {
      setCommercialPlans([]);
      return;
    }
    setLoadingPromotions(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/promotions/communes/${formData.comuna_conexion}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        enqueueSnackbar("Error al cargar los planes comerciales", {
          variant: "error",
        });
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setCommercialPlans(data);
      } else if (data && Array.isArray(data.current_promotions)) {
        setCommercialPlans(data.current_promotions);
      } else {
        enqueueSnackbar("Formato incorrecto en planes comerciales", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error de conexión al cargar promociones", {
        variant: "error",
      });
    } finally {
      setLoadingPromotions(false);
    }
  }, [formData.comuna_conexion, token, enqueueSnackbar]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  // FUNCIÓN PARA EXTRAER VELOCIDAD DE CUALQUIER NOMBRE DE PLAN
  const extraerVelocidadPlan = (nombrePlan) => {
    if (!nombrePlan) return null;

    const planLowerCase = nombrePlan.toLowerCase();
    const regexVelocidad = /(\d+)\s*(mbps|megas|mb|m)/i;
    const match = planLowerCase.match(regexVelocidad);

    if (match && match[1]) {
      return {
        velocidad: parseInt(match[1], 10),
        unidad: match[2] || "mbps",
        texto: match[0],
      };
    }

    const regexNumero = /\d+/;
    const matchNumero = planLowerCase.match(regexNumero);

    if (matchNumero) {
      return {
        velocidad: parseInt(matchNumero[0], 10),
        unidad: "mbps",
        texto: matchNumero[0],
      };
    }

    return null;
  };

  // FUNCIÓN PARA ENCONTRAR PLAN COMERCIAL POR VELOCIDAD
  const encontrarPlanComercialPorVelocidad = (planes, velocidadObjetivo) => {
    if (!planes || !planes.length || !velocidadObjetivo) return null;

    // Primero intentamos encontrar una coincidencia exacta de velocidad
    for (const plan of planes) {
      const infoPlan = extraerVelocidadPlan(plan.promotion);
      if (infoPlan && infoPlan.velocidad === velocidadObjetivo) {
        return plan;
      }
    }

    // Si no hay coincidencia exacta, buscamos el plan más cercano
    let planMasCercano = null;
    let diferenciaMenor = Infinity;

    for (const plan of planes) {
      const infoPlan = extraerVelocidadPlan(plan.promotion);
      if (infoPlan) {
        const diferencia = Math.abs(infoPlan.velocidad - velocidadObjetivo);
        if (diferencia < diferenciaMenor) {
          diferenciaMenor = diferencia;
          planMasCercano = plan;
        }
      }
    }

    return planMasCercano;
  };

  // AUTOSELECCIÓN DE PLAN POR VELOCIDAD
  useEffect(() => {
    if (
      commercialPlans.length > 0 &&
      planVelocidadExtraido &&
      !formData.plan_servicio
    ) {
      console.log(
        `🔍 Buscando plan comercial con velocidad ${planVelocidadExtraido.velocidad} ${planVelocidadExtraido.unidad}`
      );

      const planEncontrado = encontrarPlanComercialPorVelocidad(
        commercialPlans,
        planVelocidadExtraido.velocidad
      );

      if (planEncontrado) {
        console.log("✅ Plan comercial encontrado:", planEncontrado.promotion);
        handlePlanSelect({
          target: { value: planEncontrado.promotion_id.toString() },
        });
      } else {
        console.log("⚠️ No se encontró un plan comercial equivalente");
      }
    }
  }, [commercialPlans, planVelocidadExtraido]);

  // ACTUALIZAR CAMPOS RELACIONADOS AL PLAN COMERCIAL
  useEffect(() => {
    if (
      formData.plan_servicio &&
      commercialPlans.length > 0 &&
      !formData.promocion
    ) {
      const plan = commercialPlans.find(
        (p) => p.promotion_id === Number(formData.plan_servicio)
      );
      if (plan) {
        const priceRegex = /\$\s*([\d.]+)/;
        const matchPrice = plan.promotion.match(priceRegex);
        let extractedPrice = "$0.00";
        if (matchPrice && matchPrice[1]) {
          const numericValue = parseFloat(matchPrice[1]);
          if (!isNaN(numericValue)) {
            extractedPrice = "$" + numericValue.toFixed(3);
          }
        }
        let velocity = "";
        const velocityRegex = /(\d+\s*MBPS)/i;
        const matchVelocity = plan.promotion.match(velocityRegex);
        if (matchVelocity && matchVelocity[1]) {
          velocity = matchVelocity[1];
        }
        const velocityAndPrice = velocity
          ? `${velocity} X ${extractedPrice}`
          : extractedPrice;
        setFormData((prev) => ({
          ...prev,
          promocion: plan.promotion,
          renta_mensual_plan_comercial: extractedPrice,
          plan_seleccionado: plan.promotion,
          promocion_lista: "",
          velocidad_promocion: velocityAndPrice,
        }));
      }
    }
  }, [formData.plan_servicio, commercialPlans, formData.promocion]);

  // CÁLCULO DE TOTALES
  const totalRenta = useMemo(() => {
    const plan =
      parseFloat(
        (formData.renta_mensual_plan_comercial || "").replace(/[^\d.]/g, "")
      ) || 0;
    const servicios =
      parseFloat(formData.renta_mensual_servicios_adicionales) || 0;
    return (plan + servicios).toFixed(3);
  }, [
    formData.renta_mensual_plan_comercial,
    formData.renta_mensual_servicios_adicionales,
  ]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      total_renta_mensual_plan_contratado: `$${totalRenta}`,
    }));
  }, [totalRenta]);

  const totalInstalacion = useMemo(() => {
    const costoPrincipal =
      parseFloat(formData.costo_instalacion_servicio_principal) || 0;
    const costoOtros = parseFloat(formData.costo_instalacion_otros) || 0;
    return (costoPrincipal + costoOtros).toFixed(2);
  }, [
    formData.costo_instalacion_servicio_principal,
    formData.costo_instalacion_otros,
  ]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      total_costos_instalacion: totalInstalacion,
    }));
  }, [totalInstalacion]);

  const totalPagar = useMemo(() => {
    const tRenta =
      parseFloat(
        (formData.total_renta_mensual_plan_contratado || "").replace(
          /[^\d.]/g,
          ""
        )
      ) || 0;
    const tInstalacion = parseFloat(totalInstalacion) || 0;
    return (tRenta + tInstalacion).toFixed(3);
  }, [formData.total_renta_mensual_plan_contratado, totalInstalacion]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, total_a_pagar: totalPagar }));
  }, [totalPagar]);

  // Cargar firmantes disponibles
  useEffect(() => {
    const fetchActiveSigners = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signers/active`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setFirmantes(data.data || []);
      } catch (error) {
        console.error("Error al cargar firmantes:", error);
        enqueueSnackbar("Error al cargar firmantes disponibles", {
          variant: "error",
        });
      }
    };

    fetchActiveSigners();
  }, [token, enqueueSnackbar]);

  // HANDLERS DE CAMBIOS DE INPUTS Y FECHAS
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Actualización directa sin debounce
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateChange = (newValue, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: newValue ? newValue.toISOString() : null,
    }));
  };

  // FUNCIONES PARA BUSCAR CONTRATO POR RUT
  const handleRutChange = (e) => {
    let value = e.target.value;

    // Eliminar caracteres no permitidos (solo dígitos, K/k y guión)
    const cleaned = value.replace(/[^0-9kK-]/g, "");

    // Si el usuario ya escribió un guión, mantener su formato
    if (value.includes("-")) {
      setSearchRut(value);
      return;
    }

    // Si está escribiendo, permitir cualquier formato de RUT válido
    setSearchRut(cleaned);
  };

  const handleRutKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleBuscarClienteWisphub();
    }
  };
  // Esta función busca los contratos en Wisphub por RUT
  const handleBuscarClienteWisphub = async () => {
    if (!searchRut.trim()) {
      enqueueSnackbar("Ingresa un RUT válido", { variant: "warning" });
      return;
    }
    setLoadingSearch(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/digital-contracts/wisphub/by-client-rut?clientRut=${searchRut}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setContracts(data.contracts);
        enqueueSnackbar("Contratos encontrados en Wisphub", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(
          data.message || "No se encontraron contratos en Wisphub",
          {
            variant: "warning",
          }
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error al conectar con Wisphub", { variant: "error" });
    } finally {
      setLoadingSearch(false);
    }
  };

  // Función auxiliar para normalizar texto
  const normalizarTexto = (texto) => {
    if (!texto) return "";
    return texto
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Función auxiliar para buscar región por comuna
  const buscarRegionPorComuna = (comunaParaBusqueda, normalizarFn) => {
    // Mapeo de comunas a regiones
    const mapeoRegiones = {
      santiago: 7,
      providencia: 7,
      "las condes": 7,
      nunoa: 7,
      nuñoa: 7,
      // Agregar más mapeos según sea necesario
    };

    const nombreNormalizado = normalizarFn(comunaParaBusqueda);
    return mapeoRegiones[nombreNormalizado] || null;
  };

  // Función auxiliar para buscar comuna por nombre
  const buscarComunaPorNombre = (comunas, comunaParaBusqueda, normalizarFn) => {
    if (!comunas || !comunas.length) return null;

    const nombreBuscado = normalizarFn(comunaParaBusqueda);

    // Búsqueda exacta
    let comunaEncontrada = comunas.find(
      (c) => normalizarFn(c.commune_name) === nombreBuscado
    );

    // Si no hay exacta, búsqueda por inclusión
    if (!comunaEncontrada) {
      comunaEncontrada = comunas.find((c) => {
        const nombreNormalizado = normalizarFn(c.commune_name);
        return (
          nombreNormalizado.includes(nombreBuscado) ||
          nombreBuscado.includes(nombreNormalizado)
        );
      });
    }

    return comunaEncontrada;
  };

  const fetchExistingContractFolio = useCallback(
    async (clientRut) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/digital-contracts/by-client-rut?clientRut=${clientRut}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) return null;

        const data = await response.json();

        if (data.success && data.contracts?.length > 0) {
          const latestContract = [...data.contracts].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )[0];

          if (latestContract.comment) {
            const folioMatch = latestContract.comment.match(
              /Folio asignado: (\d+)/
            );
            if (folioMatch && folioMatch[1]) {
              return folioMatch[1];
            }
          }
        }
        return null;
      } catch (error) {
        console.error("Error buscando contratos:", error);
        return null;
      }
    },
    [token]
  );

  // SELECCIONAR CONTRATO A EDITAR Y CARGAR DATOS EN EL FORMULARIO
  const handleSeleccionarContrato = async (contract) => {
    console.log("Contrato seleccionado:", contract);

    // Extraer datos relevantes
    const {
      id_servicio,
      nombre,
      cedula,
      email,
      direccion,
      ciudad,
      localidad,
      telefono,
      precio_plan,
      plan_internet,
      router,
      zona,
      fecha_instalacion,
    } = contract;

    // Formatear precio y fecha
    const precioFormateado = precio_plan
      ? `$${parseFloat(precio_plan).toFixed(3)}`
      : "$0.000";

    let fechaContrato = null;
    if (fecha_instalacion) {
      try {
        const partesFecha = fecha_instalacion.split(" ")[0].split("/");
        if (partesFecha.length === 3) {
          fechaContrato = dayjs(
            `${partesFecha[2]}-${partesFecha[1]}-${partesFecha[0]}`
          );
        }
      } catch (error) {
        console.error("Error al procesar fecha:", error);
      }
    }

    // Extraer departamento si está en la dirección
    let depto = "";
    if (direccion && direccion.includes("DPTO")) {
      const match = direccion.match(/DPTO\s+(\w+)/i);
      if (match && match[1]) depto = match[1];
    }

    // Extraer la información de velocidad del plan de internet
    if (plan_internet && plan_internet.nombre) {
      console.log("📊 Plan internet del cliente:", plan_internet.nombre);
      const infoVelocidad = extraerVelocidadPlan(plan_internet.nombre);
      console.log("📊 Velocidad extraída:", infoVelocidad);
      setPlanVelocidadExtraido(infoVelocidad);
    } else {
      setPlanVelocidadExtraido(null);
    }

    // PRIMERA FASE: Establecer campos básicos que no dependen de región/comuna
    setFormData((prev) => ({
      ...prev,
      nombre_cliente: nombre || "",
      calle_facturacion: direccion || "",
      comuna_facturacion: ciudad || localidad || "",
      email: email || "",
      rut_cliente: cedula || "",
      rut_representante_legal: cedula || "",
      folio: "", // Vacío para que se genere automáticamente
      folio_nuevo: "", // Inicializar el folio nuevo
      folio_original: id_servicio ? id_servicio.toString() : "", // Guardar como referencia
      nro_cliente: id_servicio ? id_servicio.toString() : "", // Asignar id_servicio a nro_cliente
      fecha_contrato: fechaContrato,
      depto: depto,
      sector: localidad || zona?.nombre || "",
      nodo: router?.nombre || "",
      tel_movil: telefono || "",
      calle_numero: direccion || "",
      promocion: plan_internet?.nombre || "",
      renta_mensual_plan_comercial: precioFormateado,
      total_renta_mensual_plan_contratado: precioFormateado,
      total_a_pagar: precioFormateado,
      plan_seleccionado: plan_internet?.nombre || "",
      // Limpiamos estos campos para evitar datos obsoletos
      region_conexion: "",
      comuna_conexion: "",
      plan_servicio: "", // Limpiamos para que el useEffect de autoselección de plan funcione
    }));

    // Esperar a que React aplique los cambios básicos
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Buscar folio original si existe un contrato previo
    if (cedula) {
      const existingFolio = await fetchExistingContractFolio(cedula);
      if (existingFolio) {
        setFormData((prev) => ({ ...prev, folio: existingFolio }));
        console.log("Se encontró folio original:", existingFolio);
      }
    }

    // IDENTIFICAR COMUNA Y REGIÓN
    const comunaParaBusqueda = ciudad || localidad || "";
    console.log("📍 Buscando comuna:", comunaParaBusqueda);

    // Caso especial para HUARA - implementación robusta
    const esHuara =
      comunaParaBusqueda.toUpperCase() === "HUARA" ||
      (zona && zona.nombre && zona.nombre.toUpperCase() === "HUARA");

    // Caso especial para localidades en Tarapacá
    const esTarapaca =
      comunaParaBusqueda.toUpperCase() === "1-NAP05" ||
      localidad === "1-NAP05" ||
      esHuara;

    // Forzamos la región de Tarapacá (ID: 2) para estos casos especiales
    if (esTarapaca) {
      console.log(
        "🔵 CASO ESPECIAL: Asignando directamente región Tarapacá (2)"
      );

      try {
        // Establecer región de forma aislada
        setFormData((prev) => ({ ...prev, region_conexion: "2" }));

        // Esperar a que se aplique la región y se dispare useEffect
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Forzar la carga de comunas explícitamente
        console.log("🔍 Cargando comunas para región Tarapacá (2)...");
        const comunasCargadas = await loadCommunes(2);
        console.log("✅ Comunas cargadas para Tarapacá:", comunasCargadas);

        // Esperar a que se asienten las comunas en el estado
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Si es Huara, forzar directamente comuna con ID 8
        if (esHuara) {
          console.log(
            "⭐ Caso HUARA: Estableciendo directamente comuna_conexion=8"
          );

          // Verificar que la comuna 8 (Huara) exista en las comunas cargadas
          const comunaHuara = comunasCargadas.find((c) => c.commune_id === 8);

          if (comunaHuara) {
            console.log(
              "✅ VERIFICADO: Comuna Huara encontrada, ID:",
              comunaHuara.commune_id
            );

            // Establecer comuna directamente, sin pasar por búsquedas
            setFormData((prev) => ({ ...prev, comuna_conexion: "8" }));

            // Esperar a que se aplique la comuna
            await new Promise((resolve) => setTimeout(resolve, 200));

            console.log("✅ Proceso completado para Huara exitosamente");
          } else {
            console.error(
              "❌ ERROR: No se encontró Huara (ID 8) en las comunas cargadas"
            );
            enqueueSnackbar(
              "Error al cargar la comuna Huara. Selecciónela manualmente.",
              {
                variant: "warning",
              }
            );
          }
        }
        // Para otras comunas de Tarapacá, buscar por nombre normalizado
        else {
          const nombreBuscado = normalizarTexto(comunaParaBusqueda);

          // Buscar la comuna entre las cargadas
          const comunaEncontrada = comunasCargadas.find(
            (c) =>
              normalizarTexto(c.commune_name) === nombreBuscado ||
              normalizarTexto(c.commune_name).includes(nombreBuscado) ||
              nombreBuscado.includes(normalizarTexto(c.commune_name))
          );

          if (comunaEncontrada) {
            console.log("✅ Comuna encontrada:", comunaEncontrada);
            setFormData((prev) => ({
              ...prev,
              comuna_conexion: comunaEncontrada.commune_id.toString(),
            }));
          } else {
            console.log("⚠️ No se encontró la comuna:", comunaParaBusqueda);
            enqueueSnackbar(
              `No se encontró la comuna '${comunaParaBusqueda}'. Selecciónela manualmente.`,
              {
                variant: "warning",
              }
            );
          }
        }
      } catch (error) {
        console.error("❌ Error en la secuencia para Tarapacá:", error);
        enqueueSnackbar("Error al procesar la comuna. Inténtelo de nuevo.", {
          variant: "error",
        });
      }
    }
    // Para otras regiones, buscar usando el flujo normal mejorado
    else {
      let regionId = buscarRegionPorComuna(comunaParaBusqueda, normalizarTexto);

      if (regionId) {
        console.log("🔍 Región identificada:", regionId);

        try {
          // Establecer región
          setFormData((prev) => ({
            ...prev,
            region_conexion: regionId.toString(),
          }));

          // Esperar a que se dispare useEffect y cargue comunas
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Cargar comunas explícitamente
          const comunasCargadas = await loadCommunes(regionId);
          console.log("✅ Comunas cargadas:", comunasCargadas);

          // Buscar la comuna por nombre normalizado o parcial
          const comunaEncontrada = buscarComunaPorNombre(
            comunasCargadas,
            comunaParaBusqueda,
            normalizarTexto
          );

          if (comunaEncontrada) {
            console.log("✅ Comuna encontrada:", comunaEncontrada);
            setFormData((prev) => ({
              ...prev,
              comuna_conexion: comunaEncontrada.commune_id.toString(),
            }));
          } else {
            console.log("⚠️ No se encontró la comuna:", comunaParaBusqueda);
            enqueueSnackbar(
              `No se encontró la comuna '${comunaParaBusqueda}'. Selecciónela manualmente.`,
              {
                variant: "warning",
              }
            );
          }
        } catch (error) {
          console.error("❌ Error al procesar región/comuna:", error);
          enqueueSnackbar("Error al cargar comunas. Inténtelo de nuevo.", {
            variant: "error",
          });
        }
      } else {
        console.log(
          "⚠️ No se pudo identificar la región para:",
          comunaParaBusqueda
        );
        enqueueSnackbar(
          "No se pudo identificar la región. Selecciónela manualmente.",
          {
            variant: "warning",
          }
        );
      }
    }

    setSelectedContract(contract);
  };

  // HANDLER PARA SELECCIÓN DE PLAN (ACTUALIZAR CAMPOS RELACIONADOS)
  const handlePlanSelect = (e) => {
    const selectedPlanId = e.target.value;
    const plan = commercialPlans.find(
      (p) => p.promotion_id === Number(selectedPlanId)
    );
    if (plan) {
      const priceRegex = /\$\s*([\d.]+)/;
      const matchPrice = plan.promotion.match(priceRegex);
      let extractedPrice = "$0.00";
      if (matchPrice && matchPrice[1]) {
        const numericValue = parseFloat(matchPrice[1]);
        if (!isNaN(numericValue)) {
          extractedPrice = "$" + numericValue.toFixed(3);
        }
      }
      let velocity = "";
      const velocityRegex = /(\d+\s*MBPS)/i;
      const matchVelocity = plan.promotion.match(velocityRegex);
      if (matchVelocity && matchVelocity[1]) {
        velocity = matchVelocity[1];
      }
      const velocityAndPrice = velocity
        ? `${velocity} X ${extractedPrice}`
        : extractedPrice;
      setFormData((prev) => ({
        ...prev,
        plan_servicio: selectedPlanId,
        promocion: plan.promotion,
        renta_mensual_plan_comercial: extractedPrice,
        plan_seleccionado: plan.promotion,
        promocion_lista: "",
        velocidad_promocion: velocityAndPrice,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        plan_servicio: "",
        promocion: "",
        renta_mensual_plan_comercial: "",
        plan_seleccionado: "",
        velocidad_promocion: "",
      }));
    }
  };

  // NUEVA FUNCIÓN: Crear contrato nuevo para cambio de plan
  const handleCrearContratoCambioPlan = async (e) => {
    e.preventDefault();
    if (!selectedContract) {
      enqueueSnackbar("No has seleccionado ningún contrato para editar", {
        variant: "warning",
      });
      return;
    }

    if (!formData.rut_cliente || !formData.rut_representante_legal || !formData.email) {
      enqueueSnackbar("El RUT y el correo del cliente son obligatorios", {
        variant: "warning",
      });
      return;
    }

    setIsGeneratingPDF(true);
    try {
      console.log("Creando contrato de cambio de plan para:", selectedContract);

      // 1. Generar el PDF
      const pdfBase64 = await generatePdfBase64({
        ...formData,
        folio:
          formData.folio_nuevo || formData.folio || nextFolio || "(Pendiente)",
        fecha_contrato: formData.fecha_contrato
          ? typeof formData.fecha_contrato === "object"
            ? dayjs(formData.fecha_contrato).format("YYYY-MM-DD")
            : formData.fecha_contrato
          : null,
        fecha_nacimiento: formData.fecha_nacimiento
          ? typeof formData.fecha_nacimiento === "object"
            ? dayjs(formData.fecha_nacimiento).format("YYYY-MM-DD")
            : formData.fecha_nacimiento
          : null,
        primer_pago: formData.primer_pago
          ? typeof formData.primer_pago === "object"
            ? dayjs(formData.primer_pago).format("YYYY-MM-DD")
            : formData.primer_pago
          : null,
      });

      // 2. Enviar a validate-and-create
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/digital-contracts/validate-and-create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            clientRut: formData.rut_cliente,
            clientLegalRepresentativeRut: formData.rut_representante_legal,
            clientEmail: formData.email,
            pdfBase64: pdfBase64,
            contractData: {
              ...formData,
              // Guardar el ID original en observaciones_plan
              observaciones_plan: `Cambio de plan. Contrato original: ${
                formData.folio || selectedContract.id_servicio || ""
              }. ${formData.observaciones_plan || ""}`,
              es_cambio_plan: true,
              folio_original: formData.folio || "",
            },
            selectedSignerId: selectedSignerId,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        enqueueSnackbar(
          `Nuevo contrato creado y enviado a firma. Folio: ${
            data?.contract?.folio || ""
          }`,
          { variant: "success" }
        );
      } else {
        enqueueSnackbar(
          `Error al crear el nuevo contrato: ${data.message || ""}`,
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error al crear nuevo contrato:", error);
      enqueueSnackbar("Error de conexión: " + error.message, {
        variant: "error",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // FUNCIÓN PARA ALTERNAR ENTRE SELECCIONAR/DESELECCIONAR TODOS LOS DOCUMENTOS
  const handleToggleAllDocuments = () => {
    // Verifica si todos los checkboxes están seleccionados
    const allSelected =
      formData.doc_fotocopia_constitucion === true &&
      formData.doc_fotocopia_rut_representante === true &&
      formData.doc_fotocopia_cedula === true &&
      formData.doc_comprobante_domicilio === true &&
      formData.doc_condiciones_generales === true &&
      formData.doc_contrato_servicios === true &&
      formData.doc_contrato_comodato === true &&
      formData.doc_informacion_facturacion === true;

    // Invierte el estado para todos los checkboxes
    setFormData((prev) => ({
      ...prev,
      doc_fotocopia_constitucion: !allSelected,
      doc_fotocopia_rut_representante: !allSelected,
      doc_fotocopia_cedula: !allSelected,
      doc_comprobante_domicilio: !allSelected,
      doc_condiciones_generales: !allSelected,
      doc_contrato_servicios: !allSelected,
      doc_contrato_comodato: !allSelected,
      doc_informacion_facturacion: !allSelected,
    }));
  };

  // VISTA PREVIA DEL PDF
  const handlePreviewPDF = async () => {
    if (!selectedContract) {
      enqueueSnackbar("Primero selecciona un contrato para visualizar", {
        variant: "warning",
        autoHideDuration: 5000,
      });
      return;
    }

    setIsGeneratingPDF(true);
    try {
      const pdfBase64 = await generatePdfBase64({
        ...formData,
        // Asegurar que el PDF use el folio nuevo, folio, o nextFolio
        folio:
          formData.folio_nuevo || formData.folio || nextFolio || "(Pendiente)",
        fecha_contrato: formData.fecha_contrato
          ? formData.fecha_contrato.toISOString()
          : null,
        fecha_nacimiento: formData.fecha_nacimiento
          ? typeof formData.fecha_nacimiento === "object"
            ? formData.fecha_nacimiento.toISOString()
            : formData.fecha_nacimiento
          : null,
        primer_pago: formData.primer_pago
          ? formData.primer_pago.toISOString()
          : null,
      });

      // Convertir Base64 a Blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Crear URL y abrir modal
      const url = URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setPreviewOpen(true);

      enqueueSnackbar("Vista previa generada", {
        variant: "success",
        autoHideDuration: 3000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
    } catch (error) {
      console.error("Error al generar vista previa:", error);
      enqueueSnackbar("Error al generar vista previa", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Función para cerrar modal de vista previa
  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  // Función para descargar PDF desde la vista previa o directamente
  const handleDownloadPDF = async () => {
    if (pdfPreviewUrl) {
      const link = document.createElement("a");
      link.href = pdfPreviewUrl;
      link.download = "contrato_modificado.pdf";
      link.click();

      enqueueSnackbar("PDF descargado", {
        variant: "success",
        autoHideDuration: 3000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
    } else {
      try {
        const pdfBase64 = await generatePdfBase64({
          ...formData,
          fecha_contrato: formData.fecha_contrato
            ? formData.fecha_contrato.toISOString()
            : null,
          fecha_nacimiento: formData.fecha_nacimiento
            ? formData.fecha_nacimiento.toISOString()
            : null,
          primer_pago: formData.primer_pago
            ? formData.primer_pago.toISOString()
            : null,
        });
        const link = document.createElement("a");
        link.href = "data:application/pdf;base64," + pdfBase64;
        link.download = "contrato_modificado.pdf";
        link.click();

        enqueueSnackbar("PDF generado y descargado", {
          variant: "success",
          autoHideDuration: 3000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
      } catch (error) {
        console.error("Error al generar PDF:", error);
        enqueueSnackbar("Error al generar PDF", {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    }
  };

  return (
    <div className="dashboard-container">
      <Header />
      <div className="content-area">
        <Menu role_id={roleId} />
        <div className="main-content">
          <ContentContainer>
            <Typography
              variant="h4"
              align="center"
              sx={{ mb: 3, color: "#99235c", fontWeight: "bold" }}
            >
              Modificar Contrato Digital
            </Typography>

            {/* Sección de búsqueda por RUT */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={9}>
                  <TextField
                    label="Buscar Cliente por RUT (ej: 12345678-9)"
                    value={searchRut}
                    onChange={handleRutChange}
                    onKeyDown={handleRutKeyDown}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    onClick={handleBuscarClienteWisphub}
                    fullWidth
                    sx={{ backgroundColor: "#99235c" }}
                    disabled={loadingSearch}
                  >
                    {loadingSearch ? <CircularProgress size={24} /> : "Buscar"}
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Listado de contratos encontrados */}
            {contracts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Contratos encontrados:
                </Typography>
                <Grid
                  container
                  spacing={3}
                  sx={{
                    display: "flex",
                    justifyContent:
                      contracts.length === 1 ? "center" : "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  {contracts.map((contract) => {
                    // Extraer y formatear datos para mostrar en la tarjeta
                    const folio = contract.id_servicio || "N/A";
                    const planNombre =
                      contract.plan_internet?.nombre || "No especificado";
                    const precioMensual = contract.precio_plan
                      ? `$${parseFloat(contract.precio_plan).toLocaleString(
                          "es-CL"
                        )}`
                      : "$0";

                    return (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={contract.id_servicio}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          minWidth: "280px",
                        }}
                      >
                        <Paper elevation={3} sx={{ p: 2, width: "100%" }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold" }}
                          >
                            Folio: {folio}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 1,
                            }}
                          >
                            Plan: {planNombre}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            Renta Mensual: {precioMensual}
                          </Typography>
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              variant="contained"
                              onClick={() =>
                                handleSeleccionarContrato(contract)
                              }
                              sx={{
                                backgroundColor: "#99235c",
                                width: "90%",
                              }}
                            >
                              Seleccionar
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Formulario para editar el contrato seleccionado */}
            {selectedContract && (
              <Paper elevation={3} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
                <Typography
                  variant="h5"
                  sx={{ mb: 2, color: "#99235c", fontWeight: "bold" }}
                >
                  Nuevo Contrato - Cambio de Plan
                </Typography>
                {/* DATOS PERSONALES – FACTURACIÓN */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: "#99235c",
                      fontWeight: 500,
                    }}
                  >
                    Datos Personales – Facturación
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Nombre del Cliente*"
                        placeholder="Ej: Juan Pérez"
                        name="nombre_cliente"
                        value={formData.nombre_cliente || ""}
                        onChange={handleInputChange}
                        helperText="Ingrese su nombre completo"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Calle"
                        placeholder="Ej: Av. Principal #123"
                        name="calle_facturacion"
                        value={formData.calle_facturacion}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Cond./Edif."
                        placeholder="Ej: Edif. Los Robles"
                        name="cond_edif_facturacion"
                        value={formData.cond_edif_facturacion}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Comuna"
                        placeholder="Ej: Talca"
                        name="comuna_facturacion"
                        value={formData.comuna_facturacion}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Correo Electrónico"
                        placeholder="Ej: correo@dominio.cl"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Número Casa"
                        placeholder="Ej: 123A"
                        name="numero"
                        value={formData.numero}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Teléfono Trabajo"
                        placeholder="Ej: +56912345612"
                        name="tel_trabajo"
                        value={formData.tel_trabajo}
                        onChange={handleInputChange}
                      />
                    </Grid>
                  </Grid>
                </Box>
                {/* DATOS PERSONALES – CONEXIÓN */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: "#99235c",
                      fontWeight: 500,
                    }}
                  >
                    Datos Personales – Conexión
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="RUT del Cliente*"
                        placeholder="Ej: 12345678-9"
                        name="rut_cliente"
                        value={formData.rut_cliente}
                        onChange={handleInputChange}
                        helperText="Ej: 12345678-9"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Folio Original (Contrato Alta)"
                        name="folio"
                        value={formData.folio || "No hay contrato previo"}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            backgroundColor: "rgba(153, 35, 92, 0.05)",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Nuevo Folio (Cambio de Plan)"
                        name="folio_nuevo"
                        value={
                          formData.folio_nuevo || "Se generará automáticamente"
                        }
                        InputProps={{
                          readOnly: true,
                          sx: {
                            backgroundColor: "rgba(153, 35, 92, 0.05)",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Número de Cliente"
                        placeholder="Ej: 00012"
                        name="nro_cliente"
                        value={formData.nro_cliente}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          label="Fecha del Contrato"
                          inputFormat="YYYY-MM-DD"
                          value={
                            formData.fecha_contrato
                              ? dayjs(formData.fecha_contrato)
                              : null
                          }
                          onChange={(newValue) =>
                            handleDateChange(newValue, "fecha_contrato")
                          }
                          renderInput={(params) => (
                            <TextField {...params} fullWidth margin="normal" />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          label="Fecha de Nacimiento"
                          inputFormat="YYYY-MM-DD"
                          value={
                            formData.fecha_nacimiento
                              ? dayjs(formData.fecha_nacimiento)
                              : null
                          }
                          onChange={(newValue) =>
                            handleDateChange(newValue, "fecha_nacimiento")
                          }
                          renderInput={(params) => (
                            <TextField {...params} fullWidth margin="normal" />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Depto. / Apartamento"
                        placeholder="Ej: 1402"
                        name="depto"
                        value={formData.depto}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Sector"
                        placeholder="Ej: Barrio Norte"
                        name="sector"
                        value={formData.sector}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Nodo"
                        placeholder="Ej: NODO 7"
                        name="nodo"
                        value={formData.nodo}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Teléfono Móvil 1"
                        placeholder="Ej: 123456789"
                        name="tel_movil"
                        value={formData.tel_movil}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Teléfono Móvil 2"
                        placeholder="Ej: +56912345612"
                        name="tel_movil2"
                        value={formData.tel_movil2}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Teléfono Fijo"
                        placeholder="Ej: +56212345678"
                        name="tel_fijo"
                        value={formData.tel_fijo}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Calle y Número (Conexión)"
                        placeholder="Ej: Av. Principal #123"
                        name="calle_numero"
                        value={
                          formData.calle_numero || formData.calle_facturacion
                        }
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Región"
                        select
                        fullWidth
                        name="region_conexion"
                        value={formData.region_conexion}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            region_conexion: e.target.value,
                            comuna_conexion: "",
                          }))
                        }
                        margin="normal"
                      >
                        {loadingRegions ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} />
                          </MenuItem>
                        ) : (
                          regions.map((region) => (
                            <MenuItem
                              key={region.region_id}
                              value={region.region_id}
                            >
                              {region.region_name}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Comuna de Conexión"
                        select
                        fullWidth
                        name="comuna_conexion"
                        value={formData.comuna_conexion}
                        onChange={handleInputChange}
                        margin="normal"
                        disabled={!formData.region_conexion || loadingCommunes}
                      >
                        {loadingCommunes ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} />
                          </MenuItem>
                        ) : (
                          communes.map((commune) => (
                            <MenuItem
                              key={commune.commune_id}
                              value={commune.commune_id}
                            >
                              {commune.commune_name}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>
                {/* --- MÉTODO DE ENVÍO Y TIPO DE DOCUMENTO --- */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        backgroundColor: "#fff",
                        border: "1px solid #f0f0f0",
                        height: "100%",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 3,
                          color: "#99235c",
                          fontWeight: 500,
                        }}
                      >
                        Método de Envío
                      </Typography>
                      <RadioGroup
                        row
                        name="envio_documento"
                        value={formData.envio_documento}
                        onChange={handleInputChange}
                        sx={{
                          justifyContent: "space-around",
                          "& .MuiFormControlLabel-root": {
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1,
                            px: 2,
                            flex: 1,
                            mr: 2,
                            "&:last-child": { mr: 0 },
                          },
                          "& .Mui-checked": {
                            color: "#99235c",
                          },
                        }}
                      >
                        <FormControlLabel
                          value="Correo-e"
                          control={<Radio />}
                          label="Correo Electrónico"
                        />
                        <FormControlLabel
                          value="Correo Postal"
                          control={<Radio />}
                          label="Correo Postal"
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        backgroundColor: "#fff",
                        border: "1px solid #f0f0f0",
                        height: "100%",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 3,
                          color: "#99235c",
                          fontWeight: 500,
                        }}
                      >
                        Tipo de Documento
                      </Typography>
                      <RadioGroup
                        row
                        name="tipo_documento"
                        value={formData.tipo_documento}
                        onChange={handleInputChange}
                        sx={{
                          justifyContent: "space-around",
                          "& .MuiFormControlLabel-root": {
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1,
                            px: 2,
                            flex: 1,
                            mr: 2,
                            "&:last-child": { mr: 0 },
                          },
                          "& .Mui-checked": {
                            color: "#99235c",
                          },
                        }}
                      >
                        <FormControlLabel
                          value="Boleta"
                          control={<Radio />}
                          label="Boleta"
                        />
                        <FormControlLabel
                          value="Factura"
                          control={<Radio />}
                          label="Factura"
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                </Grid>
                {formData.tipo_documento === "Factura" && (
                  <Box
                    sx={{
                      mt: 2,
                      mb: 4,
                      p: 3,
                      borderRadius: 2,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      backgroundColor: "#fff",
                      border: "1px solid rgba(153, 35, 92, 0.15)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: "#99235c",
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      Datos de Facturación
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Razón Social"
                          fullWidth
                          name="razon_social"
                          value={formData.razon_social}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="RUT"
                          fullWidth
                          name="rut_cliente"
                          value={formData.rut_cliente}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Calle"
                          fullWidth
                          name="calle_facturacion"
                          value={formData.calle_facturacion}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Número"
                          fullWidth
                          name="numero"
                          value={formData.numero}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Comuna"
                          fullWidth
                          name="comuna_facturacion"
                          value={formData.comuna_facturacion}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Cond./Edif."
                          fullWidth
                          name="cond_edif_facturacion"
                          value={formData.cond_edif_facturacion}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Depto"
                          fullWidth
                          name="depto"
                          value={formData.depto}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Sector"
                          fullWidth
                          name="sector"
                          value={formData.sector}
                          onChange={handleInputChange}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
                {/* --- SERVICIOS Y PLAN COMERCIAL --- */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    border: "1px solid #f0f0f0",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: "#99235c",
                      fontWeight: 500,
                    }}
                  >
                    Servicios – Plan Comercial
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Plan Comercial Disponible"
                        select
                        fullWidth
                        name="plan_servicio"
                        value={formData.plan_servicio}
                        onChange={handlePlanSelect}
                        margin="normal"
                        disabled={
                          !formData.comuna_conexion ||
                          commercialPlans.length === 0
                        }
                      >
                        {loadingPromotions ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} />
                          </MenuItem>
                        ) : (
                          commercialPlans.map((plan) => (
                            <MenuItem
                              key={plan.promotion_id}
                              value={plan.promotion_id}
                            >
                              {plan.promotion}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Promoción"
                        placeholder="Descripción larga"
                        name="promocion"
                        value={formData.promocion}
                        multiline
                        minRows={2}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Observaciones del Plan"
                        placeholder="Detalles adicionales..."
                        name="observaciones_plan"
                        value={formData.observaciones_plan}
                        onChange={handleInputChange}
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Promoción de Lista"
                        placeholder="(se borra al seleccionar un plan)"
                        name="promocion_lista"
                        value={formData.promocion_lista}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Velocidad + Promoción"
                        placeholder="Ej: 400 MBPS X $9.995"
                        name="velocidad_promocion"
                        value={formData.velocidad_promocion}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Renta Mensual Plan Contratado"
                        name="renta_mensual_plan_comercial"
                        value={formData.renta_mensual_plan_comercial}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                {/* --- RENTA MENSUAL Y CONDICIONES DE PAGO --- */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    border: "1px solid #f0f0f0",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: "#99235c",
                      fontWeight: 500,
                    }}
                  >
                    Renta Mensual y Condiciones de Pago
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Renta Mensual Servicios Adicionales"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        name="renta_mensual_servicios_adicionales"
                        value={formData.renta_mensual_servicios_adicionales}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Total Renta Mensual Plan Contratado"
                        name="total_renta_mensual_plan_contratado"
                        value={formData.total_renta_mensual_plan_contratado}
                        InputProps={{
                          readOnly: true,
                          sx: { backgroundColor: "rgba(92, 35, 153, 0.05)" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Costo Instalación Servicio Principal"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        name="costo_instalacion_servicio_principal"
                        value={formData.costo_instalacion_servicio_principal}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Costo Instalación Otros"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        name="costo_instalacion_otros"
                        value={formData.costo_instalacion_otros}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Total Costos Instalación"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        name="total_costos_instalacion"
                        value={formData.total_costos_instalacion}
                        InputProps={{
                          readOnly: true,
                          sx: { backgroundColor: "rgba(92, 35, 153, 0.05)" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        label="Total a Pagar"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        name="total_a_pagar"
                        value={formData.total_a_pagar}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            backgroundColor: "rgba(92, 35, 153, 0.05)",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                {/* --- DOCUMENTOS ACREDITADOS --- */}
                <Box
                  className="accredited-documents"
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    border: "1px solid #f0f0f0",
                    background: "#ffffff !important",
                    transform: "none !important",
                    transition: "none !important",
                    "&:hover": {
                      transform: "none !important",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05) !important",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#99235c",
                        fontWeight: 500,
                      }}
                    >
                      Documentos Acreditados
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleToggleAllDocuments}
                      sx={{
                        backgroundColor: "#99235c",
                        "&:hover": { backgroundColor: "#7a1c49" },
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        px: 2,
                      }}
                    >
                      Seleccionar Todos
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_fotocopia_constitucion}
                            onChange={handleInputChange}
                            name="doc_fotocopia_constitucion"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Fotocopia de Constitución"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_fotocopia_rut_representante}
                            onChange={handleInputChange}
                            name="doc_fotocopia_rut_representante"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Fotocopia del RUT del Representante"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_fotocopia_cedula}
                            onChange={handleInputChange}
                            name="doc_fotocopia_cedula"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Fotocopia de la Cédula de Identidad"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_comprobante_domicilio}
                            onChange={handleInputChange}
                            name="doc_comprobante_domicilio"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Comprobante de Domicilio"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Otro Documento (detalle)"
                        placeholder="Describa el documento, si corresponde..."
                        variant="outlined"
                        fullWidth
                        name="doc_otro"
                        value={formData.doc_otro}
                        onChange={handleInputChange}
                        multiline
                        minRows={2}
                        sx={{ mt: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_condiciones_generales}
                            onChange={handleInputChange}
                            name="doc_condiciones_generales"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Acredita Condiciones Generales"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_contrato_servicios}
                            onChange={handleInputChange}
                            name="doc_contrato_servicios"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Acredita Contrato de Servicios"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_contrato_comodato}
                            onChange={handleInputChange}
                            name="doc_contrato_comodato"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Acredita Contrato de Comodato"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.doc_informacion_facturacion}
                            onChange={handleInputChange}
                            name="doc_informacion_facturacion"
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": { color: "#99235c" },
                            }}
                          />
                        }
                        label="Acredita Información de Facturación"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* --- CONFIGURACIÓN DE FIRMA --- */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#fff",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, color: "#99235c", fontWeight: 500 }}
                  >
                    Configuración de Firma
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <TextField
                          label="RUT Firmante Cliente"
                          placeholder="Ej: 12345678-9"
                          name="rut_representante_legal"
                          value={formData.rut_representante_legal}
                          onChange={handleInputChange}
                          helperText="Ej: 12345678-9"
                        />
                      </FormControl>
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="signer-select-label">
                          Firmante INGBELL
                        </InputLabel>
                        <Select
                          labelId="signer-select-label"
                          id="signer-select"
                          value={selectedSignerId || ""}
                          onChange={(e) => setSelectedSignerId(e.target.value)}
                          label="Firmante"
                        >
                          <MenuItem value="">Firmante por defecto</MenuItem>
                          {firmantes.map((firmante) => (
                            <MenuItem key={firmante.id} value={firmante.id}>
                              {firmante.first_name} {firmante.last_name} (
                              {firmante.rut})
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {selectedSignerId
                            ? "El documento será firmado por el representante seleccionado"
                            : "Se utilizará el firmante por defecto"}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                {/* BOTONES DE ACCIÓN */}
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 4,
                    mb: 2,
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handlePreviewPDF}
                      sx={{
                        px: 4,
                        py: 1.2,
                        minWidth: "200px",
                        backgroundColor: "#ffffff",
                        color: "#99235c",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: "#99235c",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          borderColor: "#7a1c49",
                        },
                        textTransform: "uppercase",
                        fontWeight: "bold",
                      }}
                      disabled={isGeneratingPDF}
                    >
                      {isGeneratingPDF ? (
                        <CircularProgress size={24} sx={{ color: "#99235c" }} />
                      ) : (
                        "Vista Previa"
                      )}
                    </Button>
                    <Button
                      variant="contained"
                      type="button"
                      onClick={handleCrearContratoCambioPlan}
                      sx={{
                        px: 4,
                        py: 1.2,
                        minWidth: "200px",
                        backgroundColor: "#99235c",
                        color: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#7a1c49",
                        },
                        textTransform: "uppercase",
                        fontWeight: "bold",
                      }}
                      disabled={isGeneratingPDF}
                    >
                      {isGeneratingPDF ? (
                        <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                      ) : (
                        "Crear Nuevo Contrato y Enviar a Sovos"
                      )}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Modal de Vista Previa del PDF */}
            <Modal
              open={previewOpen}
              onClose={handleClosePreview}
              closeAfterTransition
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Fade in={previewOpen}>
                <Box
                  sx={{
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 2,
                    width: "90%",
                    maxWidth: "850px",
                    maxHeight: "90vh",
                    borderRadius: 2,
                    outline: "none",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" component="h2">
                      Vista Previa del Documento
                    </Typography>
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={handleDownloadPDF}
                        sx={{
                          mr: 2,
                          color: "#99235c",
                          borderColor: "#99235c",
                          "&:hover": {
                            borderColor: "#7a1e49",
                            backgroundColor: "rgba(153, 35, 92, 0.04)",
                          },
                        }}
                      >
                        Descargar
                      </Button>
                      <IconButton
                        onClick={handleClosePreview}
                        aria-label="close"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      height: "calc(90vh - 100px)",
                      overflow: "hidden",
                      borderRadius: 1,
                      border: "1px solid #eaeaea",
                    }}
                  >
                    {pdfPreviewUrl && (
                      <iframe
                        ref={pdfViewerRef}
                        src={pdfPreviewUrl}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                        }}
                        title="Vista previa del PDF"
                      />
                    )}
                  </Box>
                </Box>
              </Fade>
            </Modal>
          </ContentContainer>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ModificarContratoDigitalPage;
